import { useCallback, useReducer } from 'react';

export interface GridSelectionState {
  selectedKeys: Set<string>;
  setSelectedKeys: (keys: Set<string>) => void;
  selectAll: (allKeys: string[]) => void;
  isSelected: (key: string) => boolean;
  focusedKey: string | null;
  setFocusedKey: (key: string | null) => void;
}

type GridSelectionAction =
  | { type: 'SET_SELECTED_KEYS'; keys: Set<string> }
  | { type: 'SELECT_ALL'; allKeys: string[] }
  | { type: 'SET_FOCUSED_KEY'; key: string | null };

type GridSelectionInternalState = {
  selectedKeys: Set<string>;
  focusedKey: string | null;
};

function reducer(state: GridSelectionInternalState, action: GridSelectionAction): GridSelectionInternalState {
  switch (action.type) {
    case 'SET_SELECTED_KEYS':
      return { ...state, selectedKeys: action.keys };
    case 'SELECT_ALL':
      return { ...state, selectedKeys: new Set(action.allKeys) };
    case 'SET_FOCUSED_KEY':
      return { ...state, focusedKey: action.key };
    default:
      return state;
  }
}

export function useGridSelection(): GridSelectionState {
  const [state, dispatch] = useReducer(reducer, {
    selectedKeys: new Set<string>(),
    focusedKey: null,
  });

  const setSelectedKeys = useCallback((keys: Set<string>) => {
    dispatch({ type: 'SET_SELECTED_KEYS', keys });
  }, []);

  const selectAll = useCallback((allKeys: string[]) => {
    dispatch({ type: 'SELECT_ALL', allKeys });
  }, []);

  const isSelected = useCallback((key: string) => state.selectedKeys.has(key), [state.selectedKeys]);

  const setFocusedKey = useCallback((key: string | null) => {
    dispatch({ type: 'SET_FOCUSED_KEY', key });
  }, []);

  return {
    selectedKeys: state.selectedKeys,
    setSelectedKeys,
    selectAll,
    isSelected,
    focusedKey: state.focusedKey,
    setFocusedKey,
  };
}
